import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAdmin } from '../context/AdminContext';
import { formatFecha } from '../utils/fecha';

import { API } from '../config/api';
// v23: METODOS ya no es hardcoded — viene del catálogo metodo_pago_cxp en BD.
// Fallback solo si la lista falla en cargar (lista actual: Transferencia, SINPE Móvil, Cheque, Efectivo).
const METODOS_FALLBACK = ['Transferencia', 'SINPE Móvil', 'Cheque', 'Efectivo'];
const MONEDAS = ['Colones', 'Dólares', 'Euros'];

const EMPTY = {
  factura_id: '', proveedor_id: '', fecha_pago: '', monto_pagado: '',
  moneda: 'Colones', metodo_pago_id: '', comprobante_url: '', notas: ''
};

const inputStyle = {
  padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '8px',
  fontSize: '13px', outline: 'none', width: '100%',
  boxSizing: 'border-box', fontFamily: 'Segoe UI, sans-serif'
};

const formatMonto  = (m) => m != null ? Number(m).toLocaleString('es-CR', { minimumFractionDigits: 2 }) : '0,00';
const metodoBadge  = (m) => <span style={{ background: '#f0f0f0', color: '#555', padding: '2px 8px', borderRadius: '8px', fontSize: '11px' }}>{m || '—'}</span>;
const monedaBadge  = (m) => {
  const s = m === 'Colones'
    ? { background: '#e8f4e4', color: '#27500A' }
    : { background: '#e6f1fb', color: '#185FA5' };
  return <span style={{ ...s, padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '500' }}>{m}</span>;
};

// ── Modal eliminar ─────────────────────────────────────────────────────────────

const ModalEliminar = ({ titulo, descripcion, onConfirm, onCancel }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
    <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 4v6M10 13v1" stroke="#A32D2D" strokeWidth="1.8" strokeLinecap="round"/>
            <circle cx="10" cy="10" r="8.5" stroke="#A32D2D" strokeWidth="1.4"/>
          </svg>
        </div>
        <div>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#012E40' }}>{titulo}</div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{descripcion}</div>
        </div>
      </div>
      <p style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', margin: '0 0 20px' }}>
        ¿Estás seguro que querés eliminar este registro de forma permanente? Esta acción <strong>no se puede deshacer</strong>.
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button onClick={onCancel} style={{ padding: '8px 20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>Cancelar</button>
        <button onClick={onConfirm} style={{ padding: '8px 20px', background: '#A32D2D', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Sí, eliminar</button>
      </div>
    </div>
  </div>
);

// ── Modal de pago (formulario) — exportado para uso desde Facturas ─────────────

export function ModalPago({ facturaInicial = null, facturasDisponibles = [], onGuardado, onCerrar }) {
  const isAdmin = useAdmin();

  const [form,                setForm]                = useState(EMPTY);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [saldoFactura,        setSaldoFactura]        = useState(null);   // { total, pagado, saldo_disponible, esta_pagada }
  const [saldoCotizacion,     setSaldoCotizacion]     = useState(null);   // contexto facturación vs cotización
  const [pagadoCotizacion,    setPagadoCotizacion]    = useState(null);   // { monto_total, pagado, saldo_pagar, esta_saldada }
  const [esRecurrente,        setEsRecurrente]        = useState(false);  // true si la cotización es recurrente mensual
  const [montoTocado,         setMontoTocado]         = useState(false);  // true si el usuario modificó el monto manualmente
  const [guardando,           setGuardando]           = useState(false);
  const [metodosPago,         setMetodosPago]         = useState([]);     // v23: catálogo metodo_pago_cxp [{id, valor}]

  // v23: Cargar lista de métodos de pago desde catálogo
  // El API /listas?lista=X ya filtra activo=1 server-side y devuelve [{id, valor, orden}] sin campo `activo`.
  useEffect(() => {
    axios.get(`${API}/listas?lista=metodo_pago_cxp`)
      .then(r => {
        const items = r.data || [];
        setMetodosPago(items.length ? items : METODOS_FALLBACK.map((v, i) => ({ id: -(i+1), valor: v })));
      })
      .catch(() => setMetodosPago(METODOS_FALLBACK.map((v, i) => ({ id: -(i+1), valor: v }))));
  }, []);

  // Carga saldo (factura + cotización) desde el endpoint ?action=saldo
  const cargarSaldo = useCallback(async (fid) => {
    if (!fid) { setSaldoFactura(null); setSaldoCotizacion(null); setPagadoCotizacion(null); setEsRecurrente(false); return null; }
    try {
      const res = await axios.get(`${API}/pagos-proveedor?action=saldo&factura_id=${fid}`);
      setSaldoFactura(res.data.saldo_factura || null);
      setSaldoCotizacion(res.data.saldo_cotizacion || null);
      setPagadoCotizacion(res.data.pagado_cotizacion || null);
      setEsRecurrente(!!res.data.es_recurrente);
      return res.data.saldo_factura || null;
    } catch (e) {
      setSaldoFactura(null); setSaldoCotizacion(null); setPagadoCotizacion(null); setEsRecurrente(false);
      return null;
    }
  }, []);

  // Inicializar con factura pre-cargada si viene de Facturas
  useEffect(() => {
    if (facturaInicial) {
      setForm({ ...EMPTY, factura_id: String(facturaInicial.id), proveedor_id: String(facturaInicial.proveedor_id), moneda: facturaInicial.moneda || 'Colones' });
      setFacturaSeleccionada(facturaInicial);
      setMontoTocado(false);
      cargarSaldo(facturaInicial.id).then(sf => {
        if (sf && !sf.esta_pagada) {
          // Precarga del monto con el saldo disponible (editable)
          setForm(prev => ({ ...prev, monto_pagado: String(sf.saldo_disponible) }));
        }
      });
    }
  }, [facturaInicial, cargarSaldo]);

  const seleccionarFactura = async (id) => {
    const f = facturasDisponibles.find(f => String(f.id) === String(id));
    setFacturaSeleccionada(f || null);
    setMontoTocado(false);
    setForm(prev => ({
      ...prev,
      factura_id: id,
      proveedor_id: f ? String(f.proveedor_id) : '',
      moneda: f ? f.moneda : 'Colones',
      monto_pagado: ''
    }));
    const sf = await cargarSaldo(id);
    if (sf && !sf.esta_pagada) {
      setForm(prev => ({ ...prev, monto_pagado: String(sf.saldo_disponible) }));
    }
  };

  // Handler de monto: marca el campo como "tocado" para no sobreescribirlo
  const cambiarMonto = (v) => {
    setMontoTocado(true);
    setForm(prev => ({ ...prev, monto_pagado: v }));
  };

  // Cálculo en vivo del excedente sobre factura
  const montoNum = parseFloat(form.monto_pagado) || 0;
  const saldoDisponible = saldoFactura ? saldoFactura.saldo_disponible : 0;
  const excedente = montoNum > 0 && saldoFactura ? +(montoNum - saldoDisponible).toFixed(2) : 0;
  const excede    = saldoFactura != null && excedente > 0.01;

  // Cálculo en vivo del excedente sobre cotización
  const saldoPagarCot     = pagadoCotizacion ? pagadoCotizacion.saldo_pagar : 0;
  const excedenteCot      = montoNum > 0 && pagadoCotizacion ? +(montoNum - saldoPagarCot).toFixed(2) : 0;
  const excedeCotizacion  = pagadoCotizacion != null && excedenteCot > 0.01;
  const cotizacionSaldada = pagadoCotizacion != null && pagadoCotizacion.esta_saldada;

  const guardar = async () => {
    if (!form.factura_id || !form.fecha_pago || !form.monto_pagado) {
      alert('Completá los campos obligatorios: Factura, Fecha y Monto.');
      return;
    }
    if (!form.metodo_pago_id) {
      alert('Seleccioná el método de pago.');
      return;
    }
    if (montoNum <= 0) {
      alert('El monto debe ser mayor que cero.');
      return;
    }

    const requiereForzar = excede || excedeCotizacion;

    // Si excede y NO es admin → bloqueo duro
    if (requiereForzar && !isAdmin) {
      const motivos = [];
      if (excede)           motivos.push(`saldo de factura (${saldoDisponible.toFixed(2)})`);
      if (excedeCotizacion) motivos.push(`saldo de cotización (${saldoPagarCot.toFixed(2)})`);
      alert(`El monto (${montoNum.toFixed(2)}) excede el ${motivos.join(' y el ')}.\nSolo un administrador puede forzar este pago.`);
      return;
    }

    // Si excede y sí es admin → doble confirmación
    let forzar = false;
    if (requiereForzar && isAdmin) {
      const detalles = [];
      if (excede)           detalles.push(`• Factura: excede por ${excedente.toFixed(2)} ${form.moneda} (saldo ${saldoDisponible.toFixed(2)})`);
      if (excedeCotizacion) detalles.push(`• Cotización: excede por ${excedenteCot.toFixed(2)} ${form.moneda} (saldo ${saldoPagarCot.toFixed(2)})`);
      const ok1 = window.confirm(
        `⚠ El pago excede los siguientes límites:\n\n${detalles.join('\n')}\n\nMonto a pagar: ${montoNum.toFixed(2)} ${form.moneda}\n\n¿Forzar el registro de todos modos?`
      );
      if (!ok1) return;
      const ok2 = window.confirm(
        `CONFIRMACIÓN FINAL\n\nAl forzar este pago se excederán los montos autorizados.\nEsto solo debe hacerse si existe un motivo administrativo válido.\n\n¿Continuar?`
      );
      if (!ok2) return;
      forzar = true;
    }

    setGuardando(true);
    try {
      const url = forzar ? `${API}/pagos-proveedor?force=true` : `${API}/pagos-proveedor`;
      const res = await axios.post(url, form);
      if (res?.data?.advertencia) alert('Pago registrado con advertencia: ' + res.data.advertencia);
      onGuardado && onGuardado(res.data);
    } catch (e) {
      if (e.response?.status === 409) {
        const motivo = e.response.data?.motivo === 'cotizacion' ? 'el saldo de la cotización' : 'el saldo de la factura';
        alert(`No se puede registrar: excede ${motivo}.\n\n${e.response.data?.error || ''}`);
      } else if (e.response?.status === 400) {
        alert(e.response.data?.error || 'Datos inválidos.');
      } else {
        alert('Error al registrar pago: ' + (e.response?.data?.error || e.message));
      }
    } finally {
      setGuardando(false);
    }
  };

  const campo = (label, key, tipo = 'text', opciones = null) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>{label}</label>
      {opciones
        ? <select value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inputStyle}>
            <option value=''>Seleccionar...</option>
            {opciones.map(o => <option key={o}>{o}</option>)}
          </select>
        : <input type={tipo} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inputStyle} />
      }
    </div>
  );

  // Estilos dinámicos del panel saldo
  const panelSaldoStyle = (() => {
    if (!saldoFactura) return null;
    if (saldoFactura.esta_pagada) {
      return { bg: '#FCEBEB', border: '#F7C1C1', color: '#A32D2D', label: 'Factura ya pagada' };
    }
    if (excede) {
      return { bg: '#FCEBEB', border: '#F7C1C1', color: '#A32D2D', label: 'Excede saldo' };
    }
    return { bg: '#e8f4e4', border: '#C0DD97', color: '#2d6a2d', label: 'Saldo disponible' };
  })();

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '520px', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: '#012E40', fontSize: '16px', fontWeight: '700', margin: 0 }}>Registrar pago</h3>
          <button onClick={onCerrar} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#aaa' }}>×</button>
        </div>

        {/* Selector de factura — oculto si viene pre-cargada */}
        {!facturaInicial && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '14px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>Factura *</label>
            <select value={form.factura_id || ''} onChange={e => seleccionarFactura(e.target.value)} style={inputStyle}>
              <option value=''>Seleccionar factura pendiente...</option>
              {facturasDisponibles.map(f => (
                <option key={f.id} value={f.id}>{f.numero_factura} — {f.proveedor_nombre} ({f.moneda})</option>
              ))}
            </select>
          </div>
        )}

        {/* Info de la factura seleccionada */}
        {facturaSeleccionada && (
          <div style={{ background: '#f0f7e8', border: '1px solid #C0DD97', borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', fontSize: '12px' }}>
            <div style={{ fontWeight: '600', color: '#012E40', marginBottom: '4px' }}>
              {facturaSeleccionada.numero_factura} — {facturaSeleccionada.proveedor_nombre}
            </div>
            <div style={{ color: '#555' }}>Total: {formatMonto(facturaSeleccionada.total)} {facturaSeleccionada.moneda}</div>
          </div>
        )}

        {/* Panel saldo factura (dinámico) */}
        {saldoFactura && panelSaldoStyle && (
          <div style={{
            background: panelSaldoStyle.bg, border: `1px solid ${panelSaldoStyle.border}`,
            borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', fontSize: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: panelSaldoStyle.color, fontWeight: '600' }}>{panelSaldoStyle.label}</span>
              <span style={{ color: panelSaldoStyle.color, fontWeight: '700', fontSize: '14px' }}>
                {formatMonto(saldoFactura.saldo_disponible)} {form.moneda}
              </span>
            </div>
            <div style={{ color: '#666', marginTop: '4px', fontSize: '11px' }}>
              Pagado {formatMonto(saldoFactura.pagado)} de {formatMonto(saldoFactura.total)}
            </div>
          </div>
        )}

        {/* Aviso: cotización recurrente mensual (sin tope acumulado) */}
        {esRecurrente && (
          <div style={{
            background: '#f0f7e8', border: '1px solid #C0DD97',
            borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', fontSize: '12px', color: '#2d6a2d'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '2px' }}>🔁 Cotización recurrente mensual</div>
            <div style={{ color: '#4a6a3a', fontSize: '11px' }}>
              No aplica tope de saldo acumulado contra la cotización. El control se hace a nivel de factura del mes.
            </div>
          </div>
        )}

        {/* Panel saldo cotización — facturación (contexto) */}
        {saldoCotizacion && (
          <div style={{
            background: '#e6f1fb', border: '1px solid #b8d4ea',
            borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', fontSize: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#185FA5', fontWeight: '600' }}>
                Cotización {saldoCotizacion.numero_oferta || ''} — Facturación
              </span>
              <span style={{ color: '#185FA5', fontWeight: '600' }}>
                {formatMonto(saldoCotizacion.saldo_disponible)} {form.moneda}
              </span>
            </div>
            <div style={{ color: '#666', marginTop: '4px', fontSize: '11px' }}>
              Facturado {formatMonto(saldoCotizacion.facturado)} de {formatMonto(saldoCotizacion.monto_total)}
            </div>
          </div>
        )}

        {/* Panel saldo cotización — pagos (control nuevo v16) */}
        {pagadoCotizacion && (() => {
          const panel = cotizacionSaldada || excedeCotizacion
            ? { bg: '#FCEBEB', border: '#F7C1C1', color: '#A32D2D', label: excedeCotizacion ? 'Excede saldo cotización' : 'Cotización ya saldada' }
            : { bg: '#e8f4e4', border: '#C0DD97', color: '#2d6a2d', label: 'Saldo pendiente de pago (cotización)' };
          return (
            <div style={{
              background: panel.bg, border: `1px solid ${panel.border}`,
              borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: panel.color, fontWeight: '600' }}>{panel.label}</span>
                <span style={{ color: panel.color, fontWeight: '700', fontSize: '14px' }}>
                  {formatMonto(pagadoCotizacion.saldo_pagar)} {form.moneda}
                </span>
              </div>
              <div style={{ color: '#666', marginTop: '4px', fontSize: '11px' }}>
                Pagado {formatMonto(pagadoCotizacion.pagado)} de {formatMonto(pagadoCotizacion.monto_total)}
              </div>
            </div>
          );
        })()}

        {/* Aviso dinámico cuando monto excede factura */}
        {excede && (
          <div style={{
            background: '#FCEBEB', border: '1px solid #F7C1C1',
            borderRadius: '8px', padding: '8px 14px', marginBottom: '10px', fontSize: '12px', color: '#A32D2D'
          }}>
            ⚠ El monto excede el saldo de la <strong>factura</strong> por <strong>{formatMonto(excedente)} {form.moneda}</strong>.
            {isAdmin ? ' Podés forzar el pago con doble confirmación.' : ' Solo un administrador puede forzar este pago.'}
          </div>
        )}

        {/* Aviso dinámico cuando monto excede cotización */}
        {excedeCotizacion && (
          <div style={{
            background: '#FCEBEB', border: '1px solid #F7C1C1',
            borderRadius: '8px', padding: '8px 14px', marginBottom: '14px', fontSize: '12px', color: '#A32D2D'
          }}>
            ⚠ El monto excede el saldo de la <strong>cotización</strong> por <strong>{formatMonto(excedenteCot)} {form.moneda}</strong>.
            {isAdmin ? ' Podés forzar el pago con doble confirmación.' : ' Solo un administrador puede forzar este pago.'}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {campo('Fecha de pago *', 'fecha_pago', 'date')}

          {/* Monto pagado — precargado con saldo, editable */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>
              Monto pagado * {saldoFactura && !montoTocado && (
                <span style={{ color: '#87B749', fontWeight: '400' }}>(precargado con saldo)</span>
              )}
            </label>
            <input
              type="number"
              step="0.01"
              value={form.monto_pagado || ''}
              onChange={e => cambiarMonto(e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Moneda — bloqueada a la de la factura */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>
              Moneda * <span style={{ color: '#aaa', fontWeight: '400' }}>(según factura)</span>
            </label>
            <input
              type="text"
              value={form.moneda || ''}
              disabled
              style={{ ...inputStyle, background: '#f5f6f8', color: '#555', cursor: 'not-allowed' }}
            />
          </div>

          {/* v23: combo método de pago alimentado del catálogo metodo_pago_cxp (FK) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>Método de pago *</label>
            <select
              value={form.metodo_pago_id || ''}
              onChange={e => setForm({ ...form, metodo_pago_id: e.target.value })}
              style={inputStyle}
            >
              <option value=''>Seleccionar...</option>
              {metodosPago.map(m => <option key={m.id} value={m.id}>{m.valor}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: '1/-1' }}>{campo('URL Comprobante (opcional)', 'comprobante_url')}</div>
          <div style={{ gridColumn: '1/-1' }}>{campo('Notas (opcional)', 'notas')}</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
          <button onClick={onCerrar}
            style={{ padding: '8px 20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>
            Cancelar
          </button>
          {(excede || excedeCotizacion) && isAdmin ? (
            <button onClick={guardar} disabled={guardando}
              style={{ padding: '8px 20px', background: '#A32D2D', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', opacity: guardando ? 0.7 : 1 }}>
              {guardando ? 'Guardando...' : '⚠ Forzar pago'}
            </button>
          ) : (
            <button onClick={guardar} disabled={guardando || ((excede || excedeCotizacion) && !isAdmin)}
              style={{
                padding: '8px 20px',
                background: ((excede || excedeCotizacion) && !isAdmin) ? '#ccc' : '#87B749',
                color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
                cursor: ((excede || excedeCotizacion) && !isAdmin) ? 'not-allowed' : 'pointer',
                opacity: guardando ? 0.7 : 1
              }}>
              {guardando ? 'Guardando...' : 'Registrar pago'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Componente principal Pagos ─────────────────────────────────────────────────

export default function PagosProveedor() {
  const isAdmin = useAdmin();

  const [lista,          setLista]          = useState([]);
  const [facturas,       setFacturas]       = useState([]);
  const [filtro,         setFiltro]         = useState('');
  const [modalPago,      setModalPago]      = useState(false);
  const [detalle,        setDetalle]        = useState(null);
  const [cargando,       setCargando]       = useState(true);
  const [modalEliminar,  setModalEliminar]  = useState(null);

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    setCargando(true);
    try {
      const [pa, fa] = await Promise.all([
        axios.get(`${API}/pagos-proveedor`),
        axios.get(`${API}/facturas`)
      ]);
      setLista(pa.data);
      // Solo facturas que aún admiten pagos
      setFacturas(fa.data.filter(f => f.estado !== 'Pagada'));
    } catch (e) { console.error(e); }
    setCargando(false);
  };

  const filtrados = lista.filter(p =>
    [p.proveedor_nombre, p.numero_factura, p.numero_proyecto]
      .some(v => (v || '').toLowerCase().includes(filtro.toLowerCase()))
  );

  const totalColones = lista.filter(p => p.moneda === 'Colones').reduce((s, p) => s + (parseFloat(p.monto_pagado) || 0), 0);
  const totalDolares = lista.filter(p => p.moneda === 'Dólares').reduce((s, p) => s + (parseFloat(p.monto_pagado) || 0), 0);
  const esteMes      = lista.filter(p => {
    const d = new Date(p.fecha_pago);
    const n = new Date();
    return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
  }).length;

  const confirmarEliminar = async () => {
    if (!modalEliminar) return;
    try {
      await axios.delete(`${API}/pagos-proveedor?id=${modalEliminar.id}&hard=true`);
      setModalEliminar(null);
      setDetalle(null);
      await cargar();
    } catch (e) { alert('Error al eliminar: ' + (e.response?.data?.error || e.message)); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: 'Segoe UI, sans-serif' }}>

      {/* Header */}
      <div style={{ background: '#012E40', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1 style={{ color: '#fff', fontSize: '16px', fontWeight: '600', margin: 0 }}>Pagos a Proveedores</h1>
          <span style={{ background: '#87B749', color: '#fff', fontSize: '11px', padding: '2px 10px', borderRadius: '10px', fontWeight: '500' }}>CxP</span>
        </div>
        <button onClick={() => setModalPago(true)}
          style={{ padding: '8px 18px', background: 'transparent', color: '#87B749', border: '1.5px solid #87B749', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
          + Registrar pago
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', padding: '16px 24px', background: '#f5f6f8', flexShrink: 0 }}>
        {[
          { val: lista.length,                      lbl: 'Total pagos',         color: '#012E40', grande: true  },
          { val: `₡${formatMonto(totalColones)}`,   lbl: 'Pagado en Colones',   color: '#2d6a2d', grande: false },
          { val: `$${formatMonto(totalDolares)}`,   lbl: 'Pagado en Dólares',   color: '#185FA5', grande: false },
          { val: esteMes,                           lbl: 'Pagos este mes',      color: '#f57f17', grande: true  },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '16px 20px', border: '1px solid #eee' }}>
            <div style={{ fontSize: s.grande ? '28px' : '18px', fontWeight: '700', color: s.color }}>{s.val}</div>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>{s.lbl}</div>
          </div>
        ))}
      </div>

      {/* Buscador */}
      <div style={{ padding: '0 24px 12px', background: '#f5f6f8', flexShrink: 0 }}>
        <input value={filtro} onChange={e => setFiltro(e.target.value)}
          placeholder='Buscar por proveedor, número de factura, proyecto...'
          style={{ ...inputStyle, padding: '10px 16px', fontSize: '14px', borderRadius: '10px', width: '100%' }} />
      </div>

      {/* Tabla */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px', background: '#f5f6f8' }}>
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #eee', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#012E40' }}>
                {['Fecha Pago', 'Proveedor', 'N° Factura', 'Proyecto', 'Monto Pagado', 'Moneda', 'Método', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#fff', whiteSpace: 'nowrap', borderBottom: '2px solid #87B749' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cargando
                ? <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>Cargando...</td></tr>
                : filtrados.length === 0
                  ? <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>{lista.length === 0 ? 'No hay pagos registrados aún' : 'Sin resultados'}</td></tr>
                  : filtrados.map((p, i) => (
                      <tr key={p.id}
                        style={{ borderBottom: '1px solid #f3f3f3', cursor: 'pointer' }}
                        onClick={() => setDetalle(p)}
                        onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{formatFecha(p.fecha_pago)}</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.proveedor_nombre}</td>
                        <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: '500', color: '#012E40' }}>{p.numero_factura}</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{p.numero_proyecto || '—'}</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', fontWeight: '600', color: '#012E40' }}>{formatMonto(p.monto_pagado)}</td>
                        <td style={{ padding: '10px 14px' }}>{monedaBadge(p.moneda)}</td>
                        <td style={{ padding: '10px 14px' }}>{metodoBadge(p.metodo_pago_nombre || p.metodo_pago)}</td>
                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {isAdmin && (
                              <button onClick={e => { e.stopPropagation(); setModalEliminar(p); }}
                                style={{ padding: '4px 12px', background: 'none', border: '1px solid #fcc', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', color: '#A32D2D' }}>Eliminar</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* Panel detalle */}
      {detalle && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '480px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h3 style={{ color: '#012E40', fontSize: '18px', fontWeight: '700', margin: '0 0 6px' }}>
                  Pago — {detalle.numero_factura}
                </h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {monedaBadge(detalle.moneda)}
                  {metodoBadge(detalle.metodo_pago_nombre || detalle.metodo_pago)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {isAdmin && (
                  <button onClick={() => setModalEliminar(detalle)}
                    style={{ padding: '7px 14px', background: 'none', border: '1px solid #fcc', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: '#A32D2D' }}>Eliminar</button>
                )}
                <button onClick={() => setDetalle(null)}
                  style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#aaa' }}>×</button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {[
                ['Proveedor',      detalle.proveedor_nombre],
                ['N° Factura',     detalle.numero_factura],
                ['Proyecto',       detalle.numero_proyecto || '—'],
                ['Fecha Pago',     formatFecha(detalle.fecha_pago)],
                ['Monto Pagado',   formatMonto(detalle.monto_pagado)],
                ['Metodo',         detalle.metodo_pago_nombre || detalle.metodo_pago || '-'],
              ].map(([label, val]) => (
                <div key={label} style={{ background: '#f8f8f8', borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ fontSize: '10px', color: '#aaa', marginBottom: '3px' }}>{label}</div>
                  <div style={{ fontSize: '13px', color: '#012E40', fontWeight: '500' }}>{val}</div>
                </div>
              ))}
              {detalle.notas && (
                <div style={{ gridColumn: '1/-1', background: '#f8f8f8', borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ fontSize: '10px', color: '#aaa', marginBottom: '3px' }}>Notas</div>
                  <div style={{ fontSize: '13px', color: '#012E40' }}>{detalle.notas}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal pago */}
      {modalPago && (
        <ModalPago
          facturasDisponibles={facturas}
          onGuardado={() => { setModalPago(false); cargar(); }}
          onCerrar={() => setModalPago(false)}
        />
      )}

      {/* Modal eliminar */}
      {modalEliminar && (
        <ModalEliminar
          titulo="Eliminar pago"
          descripcion={(detalle?.numero_factura || modalEliminar.numero_factura) + ' - ' + modalEliminar.proveedor_nombre}
          onConfirm={confirmarEliminar}
          onCancel={() => setModalEliminar(null)}
        />
      )}
    </div>
  );
}
